import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { services, categories, getDeposit } from "@/data/services";
import { times, buildDates } from "@/data/schedule";
import { vehicleMakeGroups, defaultVehicleMake } from "@/data/vehicleMakes";
import { activeSpecials } from "@/data/specials";
import OfferCard from "@/components/OfferCard";
import SocialLinks from "@/components/SocialLinks";
import { directionsUrl, reviewUrl } from "@/data/social";
import { trackEvent } from "@/lib/analytics";
import HeroKinetic from "@/components/editorial/HeroKinetic";
import Marquee from "@/components/editorial/Marquee";
import Manifesto from "@/components/editorial/Manifesto";
import SpotlightGallery from "@/components/editorial/SpotlightGallery";
import { Reveal } from "@/components/motion/Reveal";
import Seo, { localBusinessLd, websiteLd } from "@/components/seo/Seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialContact = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  vehicle: "",
  mileage: "",
  vin: "",
  warningLights: "",
  issue: "",
  consent: true,
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function AutoBasicCare() {
  const dates = useMemo(() => buildDates(14), []);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedServices, setSelectedServices] = useState([services[0]]);
  const [selectedDate, setSelectedDate] = useState(dates[0]);
  const [selectedTime, setSelectedTime] = useState(times[4]);
  const [vehicleType, setVehicleType] = useState("Personal Vehicle");
  const [vehicleMake, setVehicleMake] = useState(defaultVehicleMake);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [contact, setContact] = useState(initialContact);
  const [submitting, setSubmitting] = useState(false);

  const isSelected = (name) => selectedServices.some((s) => s.name === name);

  const toggleService = (svc) => {
    setSelectedServices((prev) => {
      if (prev.some((s) => s.name === svc.name)) {
        return prev.filter((s) => s.name !== svc.name);
      }
      return [...prev, svc];
    });
  };

  const totalDeposit = useMemo(
    () => selectedServices.reduce((sum, s) => sum + getDeposit(s), 0),
    [selectedServices]
  );

  const totalDurationLabel = useMemo(() => {
    if (selectedServices.length === 0) return "—";
    if (selectedServices.length === 1) return selectedServices[0].time;
    return `${selectedServices.length} services`;
  }, [selectedServices]);

  const filteredServices = useMemo(() => {
    const s = search.toLowerCase().trim();
    return services.filter((sv) => {
      const target = `${sv.name} ${sv.category}`.toLowerCase();
      const okSearch = target.includes(s);
      const okCat = category === "all" || sv.category === category;
      return okSearch && okCat;
    });
  }, [search, category]);

  const scrollToScheduler = () => {
    document.getElementById("schedule")?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollSchedulerTop = () => {
    document.querySelector(".scheduler-card")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleQuickService = (name) => {
    const found = services.find((s) => s.name === name);
    if (!found) return;
    setSelectedServices((prev) =>
      prev.some((s) => s.name === name) ? prev : [...prev, found]
    );
    setCurrentStep(2);
    setTimeout(scrollToScheduler, 50);
  };

  const handleTopServiceChip = (svc) => {
    setSelectedServices((prev) =>
      prev.some((s) => s.name === svc.name) ? prev : [...prev, svc]
    );
    toast.success(`${svc.name} added to your appointment`);
    scrollToScheduler();
  };

  const validateContact = () => {
    if (!contact.firstName.trim() || !contact.phone.trim() || !contact.vehicle.trim()) {
      toast.error("Please enter your first name, phone number, and vehicle information before continuing.");
      return false;
    }
    return true;
  };

  const validateStep1 = () => {
    if (selectedServices.length === 0) {
      toast.error("Please select at least one service.");
      return false;
    }
    return true;
  };

  const submitAppointment = async () => {
    setSubmitting(true);
    try {
      const payload = {
        services: selectedServices.map((s) => ({
          name: s.name,
          category: s.category,
          price: s.price || 0,
          time: s.time,
        })),
        vehicle_type: vehicleType,
        vehicle_make: vehicleMake,
        deposit_amount: totalDeposit,
        appointment_date: selectedDate.label,
        appointment_date_iso: selectedDate.iso,
        appointment_time: selectedTime,
        first_name: contact.firstName.trim(),
        last_name: contact.lastName.trim(),
        email: contact.email.trim(),
        phone: contact.phone.trim(),
        vehicle: contact.vehicle.trim(),
        mileage: contact.mileage.trim(),
        vin: contact.vin.trim(),
        warning_lights: contact.warningLights.trim(),
        issue: contact.issue.trim(),
        consent: contact.consent,
      };
      await axios.post(`${API}/appointments`, payload);
      trackEvent("CompleteRegistration", {
        content_type: "appointment",
        content_name: selectedServices.map((s) => s.name).join(", "),
        content_id: selectedServices.map((s) => s.name).join("|"),
        currency: "USD",
        value: 0,
      });
      toast.success("Appointment booked! A confirmation email is on its way.");
      setCurrentStep(5);
      scrollSchedulerTop();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit appointment. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const nextStep = async () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 3 && !validateContact()) return;
    if (currentStep === 1) {
      trackEvent("InitiateCheckout", {
        content_type: "appointment",
        content_name: selectedServices.map((s) => s.name).join(", "),
      });
    }
    if (currentStep === 3) {
      trackEvent("AddPaymentInfo", { content_type: "appointment" });
    }
    if (currentStep === 4) {
      await submitAppointment();
      return;
    }
    setCurrentStep((s) => Math.min(5, s + 1));
    scrollSchedulerTop();
  };

  const previousStep = () => {
    setCurrentStep((s) => Math.max(1, s - 1));
    scrollSchedulerTop();
  };

  const resetScheduler = () => {
    setCurrentStep(1);
    setSelectedServices([services[0]]);
    setSelectedDate(dates[0]);
    setSelectedTime(times[4]);
    setVehicleType("Personal Vehicle");
    setVehicleMake(defaultVehicleMake);
    setSearch("");
    setCategory("all");
    setContact(initialContact);
    scrollSchedulerTop();
  };

  useEffect(() => {
    axios.get(`${API}/`).catch(() => {});
  }, []);

  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        // small delay so layout settles after route change
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
      }
    }
  }, [location.hash, location.key]);

  // Listen for booking triggers fired by the chat widget
  useEffect(() => {
    const onBook = (e) => {
      const detail = e.detail || {};
      if (detail.service) {
        const match = services.find(
          (s) => s.name.toLowerCase() === String(detail.service).toLowerCase()
        );
        if (match) {
          setSelectedServices((prev) =>
            prev.some((s) => s.name === match.name) ? prev : [...prev, match]
          );
        }
      }
      if (detail.day) {
        const want = String(detail.day).toLowerCase().trim();
        const found = dates.find((d) => {
          const lbl = d.label.toLowerCase();
          if (want === "today") return d.isToday;
          if (want === "tomorrow") return lbl.startsWith("tomorrow") || (!d.isToday && dates[0].isToday && d === dates[1]);
          return lbl.includes(want.slice(0, 3));
        });
        if (found) setSelectedDate(found);
      }
      if (detail.time) {
        const t = String(detail.time).trim().toUpperCase().replace(/\s+/g, " ");
        const match = times.find((tt) => tt.toUpperCase() === t || tt.replace(":00", "").toUpperCase() === t.replace(":00", ""));
        if (match) setSelectedTime(match);
      }
      setCurrentStep(detail.day || detail.time ? 2 : 1);
      setTimeout(() => {
        document.getElementById("schedule")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    };
    window.addEventListener("abc:book", onBook);
    return () => window.removeEventListener("abc:book", onBook);
  }, [dates]);

  return (
    <div className="abc-app" id="top">
      <Seo
        path="/"
        title="Auto Basic Care — Auto Repair & Tires in Birmingham, AL"
        description="Dependable automotive care at an affordable price. Diagnostics, brakes, oil changes, tires, AC, suspension, and fleet maintenance in Birmingham, AL. Book online in minutes."
        jsonLd={[localBusinessLd, websiteLd]}
      />
      <Toaster position="top-center" richColors />

      <SiteHeader />

      <main>
        {/* HERO — kinetic editorial */}
        <HeroKinetic />

        {/* SIGNATURE MARQUEE */}
        <Marquee
          items={[
            "Good Prices",
            "Good Name",
            "Reliable Service",
            "Birmingham AL",
            "Since 2015",
            "Diagnostics First",
          ]}
          speed={38}
        />

        {/* SCHEDULER */}
        <section id="schedule" className="scheduler-wrap">
          <div className="container">
            <div className="scheduler-card" data-testid="scheduler-card">
              <aside className="scheduler-sidebar">
                <div>
                  <h2>Schedule an Appointment</h2>
                  <p>Answer a few questions so we can prepare the right parts, tools, and technician.</p>

                  <div className="steps">
                    {["Service", "Schedule", "Contact", "Review", "Complete"].map((label, i) => (
                      <div key={label} className="step-item">
                        <div className={`step-number ${currentStep >= i + 1 ? "active" : ""}`}>{i + 1}</div> {label}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="help-box">
                  <p>Need help?</p>
                  <h3>Call 205-405-1238</h3>
                </div>
              </aside>

              <div className="scheduler-main">
                {/* Step 1 */}
                <div className={`step-panel ${currentStep !== 1 ? "hidden" : ""}`}>
                  <h3>Select Services</h3>
                  <p className="muted">Tap any service to add or remove it. You can select as many as you need.</p>

                  <div className="responsive-controls">
                    <div>
                      <label htmlFor="serviceSearch">Search Services</label>
                      <input
                        id="serviceSearch"
                        type="text"
                        placeholder="Search oil change, brakes, AC, diagnostics..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="search-services-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="categoryFilter">Filter By Category</label>
                      <select
                        id="categoryFilter"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        data-testid="category-filter"
                      >
                        {categories.map((c) => (
                          <option key={c} value={c}>{c === "all" ? "All Categories" : c}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="selected-summary">
                    <div>
                      <div className="eyebrow">Selected Services ({selectedServices.length})</div>
                      <h2 data-testid="selected-service-title">
                        {selectedServices.length === 0
                          ? "No services selected"
                          : selectedServices.length === 1
                          ? selectedServices[0].name
                          : `${selectedServices[0].name} +${selectedServices.length - 1} more`}
                      </h2>
                      <p className="muted">{totalDurationLabel}</p>
                    </div>
                    {selectedServices.length > 1 && (
                      <button
                        className="btn btn-soft"
                        onClick={() => setSelectedServices([])}
                        data-testid="clear-services-btn"
                      >
                        Clear all
                      </button>
                    )}
                  </div>

                  {selectedServices.length > 0 && (
                    <div className="selected-chips" data-testid="selected-chips">
                      {selectedServices.map((s) => (
                        <span key={s.name} className="selected-chip">
                          <strong>{s.name}</strong>
                          <button
                            type="button"
                            aria-label={`Remove ${s.name}`}
                            onClick={() => toggleService(s)}
                            data-testid={`remove-chip-${slug(s.name)}`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="service-grid">
                    {filteredServices.length === 0 ? (
                      <div className="info-card">
                        <strong>No services found.</strong>
                        <p className="muted">Try another search term or choose All Categories.</p>
                      </div>
                    ) : (
                      filteredServices.map((svc) => {
                        const checked = isSelected(svc.name);
                        return (
                          <button
                            key={svc.name}
                            className={`service-option ${checked ? "selected" : ""}`}
                            onClick={() => toggleService(svc)}
                            aria-pressed={checked}
                            data-testid={`service-option-${slug(svc.name)}`}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14 }}>
                              <div style={{ minWidth: 0, display: "flex", gap: 12, alignItems: "flex-start" }}>
                                <span className={`svc-checkbox ${checked ? "checked" : ""}`} aria-hidden="true">
                                  {checked ? "✓" : ""}
                                </span>
                                <div style={{ minWidth: 0 }}>
                                  <strong>{svc.name}</strong>
                                  <div className="service-meta">{svc.category} • {svc.time}</div>
                                  <p style={{ marginTop: 8, color: "#64748b", fontSize: 13, lineHeight: 1.45 }}>
                                    Professional inspection and quality service performed by Auto Basic Care technicians.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>

                  <div className="addon-grid">
                    <div>
                      <label htmlFor="fleetOption">Vehicle Type</label>
                      <select
                        id="fleetOption"
                        value={vehicleType}
                        onChange={(e) => setVehicleType(e.target.value)}
                        data-testid="vehicle-type-select"
                      >
                        <option>Personal Vehicle</option>
                        <option>Commercial Vehicle</option>
                        <option>Fleet Vehicle</option>
                        <option>Work Truck</option>
                        <option>Van / Cargo Vehicle</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="vehicleMake">Vehicle Brand</label>
                      <select
                        id="vehicleMake"
                        value={vehicleMake}
                        onChange={(e) => setVehicleMake(e.target.value)}
                        data-testid="vehicle-make-select"
                      >
                        {vehicleMakeGroups.map((group) => (
                          <optgroup key={group.label} label={group.label}>
                            {group.makes.map((m) => (
                              <option key={m} value={m}>{m}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="warranty-box-step">
                    <div>
                      <div className="eyebrow">Warranty Policy</div>
                      <h2>We stand behind our service.</h2>
                      <p className="muted">
                        At Auto Basic Care, we stand behind our service with industry-standard
                        warranty coverage on select repairs, tires, and batteries. For more
                        information regarding warranty terms, coverage details, inspections, and
                        eligibility requirements, please click the link below to learn more about
                        our warranty policies and protection plans.
                      </p>
                      <Link to="/faq#faq-warranty" className="btn btn-primary warranty-cta" data-testid="warranty-learn-more">
                        Learn About Warranty Coverage
                      </Link>
                    </div>
                    <div>
                      <strong style={{ display: "block", marginBottom: 8, color: "var(--dark)" }}>Why Customers Book Online</strong>
                      <p className="muted">Faster check-ins, better service preparation, and priority scheduling.</p>
                    </div>
                  </div>

                  <div className="footer-buttons">
                    <span></span>
                    <button className="btn btn-primary" onClick={nextStep} data-testid="step1-continue-btn">
                      Continue To Scheduling
                    </button>
                  </div>
                </div>

                {/* Step 2 */}
                <div className={`step-panel ${currentStep !== 2 ? "hidden" : ""}`}>
                  <h3>Pick Date & Time</h3>
                  <p className="muted">Available appointments for Auto Basic Care.</p>

                  <div className="choice-grid">
                    {dates.map((d) => (
                      <button
                        key={d.iso}
                        className={`choice-option ${d.iso === selectedDate.iso ? "selected" : ""}`}
                        onClick={() => setSelectedDate(d)}
                        data-testid={`date-${d.iso}`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>

                  <div className="time-grid">
                    {times.map((t) => (
                      <button
                        key={t}
                        className={`choice-option ${t === selectedTime ? "selected" : ""}`}
                        onClick={() => setSelectedTime(t)}
                        data-testid={`time-${slug(t)}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>

                  <div className="footer-buttons">
                    <button className="btn btn-soft" onClick={previousStep} data-testid="step2-back-btn">Back</button>
                    <button className="btn btn-primary" onClick={nextStep} data-testid="step2-continue-btn">Continue</button>
                  </div>
                </div>

                {/* Step 3 */}
                <div className={`step-panel ${currentStep !== 3 ? "hidden" : ""}`}>
                  <h3>Contact & Vehicle Info</h3>
                  <p className="muted">This helps us confirm your appointment and prepare for the repair.</p>

                  <div className="form-grid">
                    <label>First Name
                      <input type="text" placeholder="First Name" value={contact.firstName}
                        onChange={(e) => setContact({ ...contact, firstName: e.target.value })}
                        data-testid="contact-firstname-input" />
                    </label>
                    <label>Last Name
                      <input type="text" placeholder="Last Name" value={contact.lastName}
                        onChange={(e) => setContact({ ...contact, lastName: e.target.value })}
                        data-testid="contact-lastname-input" />
                    </label>
                    <label>Email
                      <input type="email" placeholder="Email" value={contact.email}
                        onChange={(e) => setContact({ ...contact, email: e.target.value })}
                        data-testid="contact-email-input" />
                    </label>
                    <label>Phone
                      <input type="tel" placeholder="Phone" value={contact.phone}
                        onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                        data-testid="contact-phone-input" />
                    </label>
                    <label>Vehicle Year / Make / Model
                      <input type="text" placeholder="Example: 2018 Toyota Camry" value={contact.vehicle}
                        onChange={(e) => setContact({ ...contact, vehicle: e.target.value })}
                        data-testid="contact-vehicle-input" />
                    </label>
                    <label>Mileage
                      <input type="text" placeholder="Example: 92,000" value={contact.mileage}
                        onChange={(e) => setContact({ ...contact, mileage: e.target.value })}
                        data-testid="contact-mileage-input" />
                    </label>
                    <label>VIN Optional
                      <input type="text" placeholder="VIN number if available" value={contact.vin}
                        onChange={(e) => setContact({ ...contact, vin: e.target.value })}
                        data-testid="contact-vin-input" />
                    </label>
                    <label>Warning Lights?
                      <input type="text" placeholder="Example: check engine, ABS, battery" value={contact.warningLights}
                        onChange={(e) => setContact({ ...contact, warningLights: e.target.value })}
                        data-testid="contact-warning-input" />
                    </label>
                  </div>

                  <label>What issue are you experiencing?</label>
                  <textarea
                    placeholder="Example: squeaking brakes, check engine light, overheating, vibration, battery trouble..."
                    value={contact.issue}
                    onChange={(e) => setContact({ ...contact, issue: e.target.value })}
                    data-testid="contact-issue-input"
                  />

                  <label className="checkbox-row">
                    <input
                      type="checkbox"
                      checked={contact.consent}
                      onChange={(e) => setContact({ ...contact, consent: e.target.checked })}
                      data-testid="contact-consent-checkbox"
                    />
                    I agree to receive appointment confirmation calls, texts, or emails from Auto Basic Care. Message and data rates may apply.
                  </label>

                  <div className="footer-buttons">
                    <button className="btn btn-soft" onClick={previousStep} data-testid="step3-back-btn">Back</button>
                    <button className="btn btn-primary" onClick={nextStep} data-testid="step3-continue-btn">Continue</button>
                  </div>
                </div>

                {/* Step 4 */}
                <div className={`step-panel ${currentStep !== 4 ? "hidden" : ""}`}>
                  <h3>Review Details</h3>
                  <p className="muted">Confirm your appointment details before submitting.</p>

                  <div className="review-grid">
                    <div className="review-box">
                      <div className="review-title">Store</div>
                      <p>Auto Basic Care</p>
                      <p>1700 31st Street SW</p>
                      <p>Birmingham, AL 35221</p>
                    </div>
                    <div className="review-box">
                      <div className="review-title">Appointment</div>
                      <p data-testid="review-date">{selectedDate.label}</p>
                      <p data-testid="review-time">{selectedTime}</p>
                      <p>{totalDurationLabel}</p>
                    </div>
                    <div className="review-box">
                      <div className="review-title">Services Requested ({selectedServices.length})</div>
                      {selectedServices.length === 0 ? (
                        <p>No services selected</p>
                      ) : (
                        <ul style={{ paddingLeft: 18, margin: 0 }} data-testid="review-services-list">
                          {selectedServices.map((s) => (
                            <li key={s.name} style={{ padding: "3px 0", fontWeight: 700, color: "#334155" }}>
                              {s.name} <span style={{ color: "#64748b", fontWeight: 500 }}>· {s.category}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      <p style={{ marginTop: 8 }}>{vehicleType}{vehicleMake ? ` · ${vehicleMake}` : ""}</p>
                      <p>{contact.issue || "No issue notes added"}</p>
                    </div>
                    <div className="review-box">
                      <div className="review-title">Contact Info</div>
                      <p data-testid="review-name">{`${contact.firstName} ${contact.lastName}`.trim() || "Name needed"}</p>
                      <p>{contact.email || "Email not provided"}</p>
                      <p>{contact.phone || "Phone needed"}</p>
                      <p>{contact.mileage ? `${contact.vehicle} • ${contact.mileage} miles` : (contact.vehicle || "Vehicle needed")}</p>
                    </div>
                  </div>

                  <div className="payment-box">
                    <div>
                      <div className="eyebrow">Confirm Appointment</div>
                      <h2 data-testid="deposit-text">Ready to submit</h2>
                      <p className="muted">A team member will reach out to confirm details. Final pricing is reviewed in person after inspection.</p>
                    </div>
                    <button className="btn btn-primary" onClick={nextStep} disabled={submitting} data-testid="submit-appointment-btn">
                      {submitting ? "Submitting..." : "Submit Appointment"}
                    </button>
                  </div>

                  <div className="footer-buttons">
                    <button className="btn btn-soft" onClick={previousStep} data-testid="step4-back-btn">Back</button>
                    <span></span>
                  </div>
                </div>

                {/* Step 5 */}
                <div className={`step-panel ${currentStep !== 5 ? "hidden" : ""}`}>
                  <div className="success-screen">
                    <div className="success-icon">✓</div>
                    <h3>Appointment Scheduled</h3>
                    <p style={{ fontSize: "clamp(18px, 2vw, 24px)", fontWeight: 900, marginTop: 10 }} data-testid="success-date-time">
                      {selectedDate.label} at {selectedTime}
                    </p>
                    <p className="muted" style={{ maxWidth: 680, marginTop: 14 }}>
                      Thank you for booking with Auto Basic Care. A confirmation email has been sent and a team member will call or text to confirm your service.
                    </p>
                    <div className="tip-box">
                      <strong>Services scheduled</strong>
                      <ul style={{ paddingLeft: 18, marginTop: 8 }}>
                        {selectedServices.map((s) => (
                          <li key={s.name} style={{ padding: "2px 0", fontWeight: 700, color: "#0f172a" }}>{s.name}</li>
                        ))}
                      </ul>
                    </div>
                    <button className="btn btn-dark" style={{ marginTop: 26 }} onClick={resetScheduler} data-testid="book-another-btn">
                      Book Another Appointment
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* MANIFESTO — Four numbered chapters */}
        <Manifesto />

        {/* SERVICES (features) */}
        <section id="services" className="section">
          <div className="container">
            <div className="section-header">
              <div>
                <div className="eyebrow">What We Service</div>
                <h2 className="section-title">Built for personal vehicles, work trucks, and local fleets.</h2>
              </div>
            </div>

            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon">🚗</div>
                <h3>Vehicle Care</h3>
                <p className="muted">Oil changes, inspections, cooling flushes, filters, and routine maintenance.</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">🔧</div>
                <h3>Repair Services</h3>
                <p className="muted">Diagnostics, brakes, batteries, steering, suspension, and repair quotes.</p>
              </div>
              <div className="feature-card" id="fleet">
                <div className="feature-icon">🛡️</div>
                <h3>Fleet Ready</h3>
                <p className="muted">Maintenance support for small businesses, contractors, work trucks, and local fleets.</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">⭐</div>
                <h3>Quality Service</h3>
                <p className="muted">Clear communication, professional recommendations, and dependable care.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CURRENT SPECIALS */}
        <section className="section" style={{ background: "var(--cream)" }}>
          <div className="container">
            <Reveal>
              <div className="section-header">
                <div>
                  <div className="eyebrow abc-mono-label">— Current Specials</div>
                  <h2 className="section-title">Save on services you probably already need.</h2>
                </div>
                <Link to="/specials" className="btn btn-soft" data-testid="home-see-all-specials">
                  See All Specials →
                </Link>
              </div>
            </Reveal>
            <div className="offer-grid offer-grid-2">
              {activeSpecials()
                .filter((s) => s.category === "brakes" || s.badge === "Most Popular")
                .slice(0, 2)
                .map((o, i) => (
                  <Reveal key={o.id} delay={0.1 + i * 0.08}>
                    <OfferCard {...o} />
                  </Reveal>
                ))}
            </div>
          </div>
        </section>

        {/* VEHICLES WE SERVICE */}
        <section className="section abc-section-bone" style={{ background: "var(--bone)" }}>
          <div className="container">
            <Reveal>
              <div className="section-header">
                <div>
                  <div className="eyebrow abc-mono-label">— Vehicles We Service</div>
                  <h2 className="section-title">Domestic. Asian. European.</h2>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="muted" style={{ maxWidth: 780, marginBottom: 22 }}>
                Our team services many domestic, Asian, and European vehicles. Contact us to confirm service availability for your specific year, make, model, and repair request.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="brand-cards">
                {["Chevrolet","Nissan","Hyundai","Cadillac","Lexus","Audi","Mercedes-Benz","BMW","Honda","Toyota","Acura","Ford"].map((b) => (
                  <div key={b} className="brand-plate" data-testid={`brand-${b.toLowerCase()}`}>{b}</div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* SPOTLIGHT GALLERY — Editorial photography */}
        <SpotlightGallery />

        {/* CONNECT WITH AUTO BASIC CARE */}
        <section className="section abc-section-bone">
          <div className="container">
            <Reveal>
              <div className="section-header">
                <div>
                  <div className="eyebrow abc-mono-label">— Connect With Auto Basic Care</div>
                  <h2 className="section-title">Follow along for tips, specials, and shop news.</h2>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <p className="muted" style={{ maxWidth: 780, marginBottom: 20 }}>
                Follow Auto Basic Care for vehicle maintenance tips, repair updates, customer specials, shop news, and automotive education.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <SocialLinks variant="cards" />
            </Reveal>
          </div>
        </section>

        {/* MAP & DIRECTIONS */}
        <section className="section abc-section-cream" id="location">
          <div className="container">
            <Reveal>
              <div className="section-header">
                <div>
                  <div className="eyebrow abc-mono-label">— Find Us</div>
                  <h2 className="section-title">Auto Basic Care · Birmingham, AL</h2>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="map-grid">
                <div className="map-card">
                  <iframe
                    title="Auto Basic Care on Google Maps"
                    src="https://www.google.com/maps?q=1700+31st+Street+SW,+Birmingham,+AL+35221&output=embed"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
                <aside className="map-info">
                  <span className="abc-mono-label" style={{ color: "var(--ink)", opacity: 0.55 }}>— Shop</span>
                  <h3>Auto Basic Care</h3>
                  <p style={{ fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: "clamp(18px,1.6vw,22px)", margin: "12px 0 4px" }}>1700 31st Street Southwest</p>
                  <p style={{ fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: "clamp(18px,1.6vw,22px)", margin: 0 }}>Birmingham, Alabama 35221</p>
                  <p className="muted" style={{ marginTop: 12 }}>Mon–Sat 8:00 AM – 4:30 PM · Closed Sunday</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 20 }}>
                    <a href={directionsUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary" data-testid="map-directions-btn" onClick={() => trackEvent("Contact", { source: "map-directions" })}>Get Directions →</a>
                    <a href={reviewUrl} target="_blank" rel="noopener noreferrer" className="btn btn-dark" data-testid="map-review-btn" onClick={() => trackEvent("Lead", { content_type: "review", source: "map" })}>★ Leave a Google Review</a>
                    <a href="tel:2054051238" className="btn btn-soft" data-testid="map-call-btn">Call 205-405-1238</a>
                  </div>
                </aside>
              </div>
            </Reveal>
          </div>
        </section>

        {/* TOP 30 SERVICES */}
        <section className="section service-list-section">
          <div className="container">
            <div className="section-header">
              <div>
                <div className="eyebrow" style={{ color: "#facc15" }}>Top 30 Services</div>
                <h2 className="section-title">Select from the most requested auto services.</h2>
              </div>
              <a href="#schedule" className="btn btn-primary" data-testid="top-services-book-btn">Book Now</a>
            </div>
            <div className="service-columns">
              {services.map((s) => (
                <button
                  key={s.name}
                  className={`service-chip ${isSelected(s.name) ? "selected" : ""}`}
                  onClick={() => handleTopServiceChip(s)}
                  data-testid={`top-service-${slug(s.name)}`}
                >
                  {isSelected(s.name) ? "✓ " : "+ "}{s.name}
                </button>
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
